/**
 * Net Exposure / workstation market source tags.
 *
 * Client: Betfair → (BF); custom BM / custom session → (CM).
 * Replaces Lab Artemis (PB) suffixes on these surfaces.
 */

import { isBetfairStyleMarketId } from '@/api/map/books'
import { isLineSessionBetListMarket } from './workstation/lineSessionBetOdds'

export type MarketSourceTag = 'BF' | 'CM'

const SOURCE_SUFFIX_RE = /\s*\((?:PB|BF|CM)\)\s*$/i

export type MarketSourceInput = {
  marketId?: string
  marketName?: string
  productType?: string
  /** Net Exposure / workstation layout hint. */
  type?: 'match' | 'fancy' | 'bookmaker'
  isMatchOdds?: boolean
  bookMakerforNetExposure?: boolean
  bookmakerFlag?: boolean
  lineLadder?: boolean
  key?: string
}

/** Strip a trailing (PB)/(BF)/(CM) so we can re-tag cleanly. */
export function stripMarketSourceSuffix(marketName: string): string {
  return String(marketName || '')
    .replace(SOURCE_SUFFIX_RE, '')
    .trim()
}

export function formatMarketSourceLabel(
  marketName: string,
  tag: MarketSourceTag,
): string {
  const base = stripMarketSourceSuffix(marketName) || marketName.trim()
  if (!base) return `(${tag})`
  return `${base} (${tag})`
}

/**
 * Betfair exchange id: numeric.dot, no `#` BM and no `-` fancy suffix.
 * (`isBetfairStyleMarketId` alone treats fancy `1.x-1` as Betfair.)
 */
function isBetfairExchangeMarketId(marketId: string | undefined): boolean {
  const id = String(marketId || '')
  if (!isBetfairStyleMarketId(id)) return false
  return !id.includes('-')
}

function betfairIdBase(marketId: string): string {
  return marketId.split(/[#-]/)[0] || marketId
}

/** True when the id looks like Betfair exchange BM (`1.234#bm`). */
function isBetfairExchangeBookmakerId(marketId: string | undefined): boolean {
  const id = String(marketId || '')
  if (!id.includes('#')) return false
  const base = betfairIdBase(id)
  return base.includes('.') && /^\d/.test(base)
}

/**
 * Resolve BF vs CM for a market row.
 * productType wins when present; else layout flags + marketId heuristics.
 */
export function marketSourceTag(input: MarketSourceInput): MarketSourceTag {
  const product = String(input.productType || '')
    .trim()
    .toLowerCase()
  const name = String(input.marketName || '')
  const id = String(input.marketId || '')

  if (
    product === 'bookmaker' ||
    product === 'custom_session' ||
    product === 'fancy' ||
    product === 'artemis'
  ) {
    return 'CM'
  }
  if (
    product === 'betfair' ||
    product === 'line_ladder' ||
    product === 'lineladder' ||
    product === 'match_odds' ||
    product === 'matchodds'
  ) {
    return 'BF'
  }

  if (input.lineLadder || isLineSessionBetListMarket(name)) return 'BF'

  if (input.type === 'fancy') return 'CM'
  if (id.includes('-') && !id.includes('#')) return 'CM'

  if (
    input.isMatchOdds ||
    input.type === 'match' ||
    /^match odds$/i.test(stripMarketSourceSuffix(name))
  ) {
    return 'BF'
  }

  if (
    input.bookMakerforNetExposure ||
    input.bookmakerFlag ||
    input.type === 'bookmaker' ||
    id.includes('#') ||
    /bookmaker/i.test(name)
  ) {
    return isBetfairExchangeBookmakerId(id) ? 'BF' : 'CM'
  }

  if (isBetfairExchangeMarketId(id)) return 'BF'
  return 'CM'
}

/** Display name with (BF)/(CM) — never (PB). */
export function labeledMarketName(
  market: MarketSourceInput & { marketName: string },
  type?: MarketSourceInput['type'],
): string {
  const tag = marketSourceTag({ ...market, type: type ?? market.type })
  return formatMarketSourceLabel(market.marketName, tag)
}
